/**
 * @description Super Admin sidebar active-item helpers (Channel Partners default)
 *
 * Key features:
 * - Channel Partners is the landing path until a dashboard exists
 * - Under-CP `/tenants/{id}` (channelPartnerId set) keeps Channel Partners selected
 * - LynxPay `/tenants` list and `/tenants/{id}` without a CP keep Sales Orgs selected
 * - Channel Partners + Sales Orgs use this helper for visual active and aria-current
 *
 * @dependencies
 * - None (pure pathname helpers)
 *
 * @notes
 * - Click-into reuses `/tenants/{salesOrgId}`; highlight is not the route prefix
 * - Preferred names: channelPartnerId / salesOrgId
 * - Do not flip cpResolution
 */

export const ADMIN_DEFAULT_PATH = '/channel-partners';

/**
 * @param {string} pathname
 * @returns {boolean}
 */
export function isTenantDetailPath(pathname) {
  const parts = String(pathname || '').split('/').filter(Boolean);
  return parts[0] === 'tenants' && Boolean(parts[1]) && parts[1] !== 'new';
}

/**
 * @param {string} pathname
 * @param {string|null|undefined} tenantChannelPartnerId
 * @returns {boolean}
 */
export function isChannelPartnersNavActive(pathname, tenantChannelPartnerId) {
  const path = String(pathname || '');
  if (path === '/channel-partners' || path.startsWith('/channel-partners/')) {
    return true;
  }
  return isTenantDetailPath(path) && Boolean(tenantChannelPartnerId);
}

/**
 * @param {string} pathname
 * @param {string|null|undefined} tenantChannelPartnerId
 * @returns {boolean}
 */
export function isSalesOrgsNavActive(pathname, tenantChannelPartnerId) {
  const path = String(pathname || '');
  if (path !== '/tenants' && !path.startsWith('/tenants/')) {
    return false;
  }
  if (isTenantDetailPath(path) && tenantChannelPartnerId) {
    return false;
  }
  if (isTenantDetailPath(path) && tenantChannelPartnerId === undefined) {
    return false;
  }
  return true;
}

/**
 * Channel Partners and Sales Orgs do not use NavLink prefix-match.
 * @param {string} itemPath
 * @returns {boolean}
 */
export function usesAdminNavItemSoT(itemPath) {
  return itemPath === '/channel-partners' || itemPath === '/tenants';
}

/**
 * @param {boolean} isActive
 * @returns {'page'|undefined}
 */
export function adminNavAriaCurrent(isActive) {
  return isActive ? 'page' : undefined;
}

/**
 * @param {string} itemPath
 * @param {string} pathname
 * @param {string|null|undefined} tenantChannelPartnerId
 * @returns {boolean}
 */
export function isAdminNavItemActive(itemPath, pathname, tenantChannelPartnerId) {
  if (itemPath === '/channel-partners') {
    return isChannelPartnersNavActive(pathname, tenantChannelPartnerId);
  }
  if (itemPath === '/tenants') {
    return isSalesOrgsNavActive(pathname, tenantChannelPartnerId);
  }
  const path = String(pathname || '');
  return path === itemPath || path.startsWith(`${itemPath}/`);
}
